window.FamilyStockGuard = window.FamilyStockGuard || (() => {
  const normaliseSku = (sku) => String(sku || '').trim().replace(/\(\d+\)\s*$/, '');
  const member = (sku) => {
    const clean = normaliseSku(sku);
    const match = clean.match(/^(.*)_([1-9]\d*)$/);
    return match
      ? { familyKey: match[1].toUpperCase(), pack: Number(match[2]), memberSku: clean.toUpperCase() }
      : { familyKey: clean.toUpperCase(), pack: 1, memberSku: clean.toUpperCase() };
  };
  const rows = (root = document) => [...root.querySelectorAll('.cart-item[data-family-sku]')].map((row) => {
    const info = member(row.dataset.familySku);
    const input = row.querySelector('.quantity__input');
    return {
      row,
      ...info,
      quantity: Math.max(0, Number(input?.value || 0)),
      stockQuantity: Math.max(0, Number(row.dataset.familyStockQuantity || 0))
    };
  });
  const rowCapacity = (familyKey, extraUnits = 0) => {
    let capacity = Math.max(0, Number(extraUnits || 0));
    for (const item of rows(document)) {
      if (item.familyKey === familyKey) capacity = Math.max(capacity, item.stockQuantity * item.pack);
    }
    return capacity;
  };
  const clampCartLine = (root, line, requested) => {
    const wanted = Math.max(0, Number.parseInt(requested, 10) || 0);
    const row = root.querySelector(`#CartItem-${line}, #CartDrawer-Item-${line}`);
    if (!row || !row.dataset.familySku) return wanted;
    const current = member(row.dataset.familySku);
    const family = rows(root).filter((item) => item.familyKey === current.familyKey);
    if (!family.length) return wanted;
    const capacity = Math.max(...family.map((item) => item.stockQuantity * item.pack));
    const usedByOthers = family
      .filter((item) => item.row !== row)
      .reduce((sum, item) => sum + item.quantity * item.pack, 0);
    const maximum = Math.max(0, Math.floor((capacity - usedByOthers) / current.pack));
    return Math.min(wanted, maximum);
  };
  const variantRows = (form) => {
    const node = form.querySelector('[data-family-stock-variants]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const limitProductAdd = async (form, variantId, requested) => {
    const variants = variantRows(form);
    const selected = variants.find((v) => String(v.id) === String(variantId));
    const wanted = Math.max(1, Number.parseInt(requested, 10) || 1);
    if (!selected?.sku) return { quantity: wanted, limited: false };
    const info = member(selected.sku);
    const managed = variants.filter((v) => {
      const vi = member(v.sku);
      return vi.familyKey === info.familyKey && String(v.inventory_management || '').toLowerCase() === 'shopify' && String(v.inventory_policy || '').toLowerCase() !== 'continue';
    });
    if (!managed.length) return { quantity: wanted, limited: false };
    let capacity = Math.max(...managed.map((v) => Math.max(0, Number(v.inventory_quantity || 0)) * member(v.sku).pack));
    capacity = rowCapacity(info.familyKey, capacity);
    const cartUrl = (window.routes?.cart_url || '/cart') + '.js';
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to read the cart for family stock validation.');
    const cart = await response.json();
    const existingUnits = (cart.items || []).reduce((sum, item) => {
      const ci = member(item.sku);
      return ci.familyKey === info.familyKey ? sum + Math.max(0, Number(item.quantity || 0)) * ci.pack : sum;
    }, 0);
    const maximum = Math.max(0, Math.floor((capacity - existingUnits) / info.pack));
    return { quantity: Math.min(wanted, maximum), limited: wanted > maximum, requested: wanted, maximum, capacity, existingUnits };
  };
  const firstViolation = (root) => {
    const grouped = new Map();
    for (const item of rows(root)) {
      const group = grouped.get(item.familyKey) || { capacity: 0, used: 0 };
      group.capacity = Math.max(group.capacity, item.stockQuantity * item.pack);
      group.used += item.quantity * item.pack;
      grouped.set(item.familyKey, group);
    }
    for (const [familyKey, group] of grouped) {
      if (group.used > group.capacity) return { familyKey, ...group };
    }
    return null;
  };
  if (!window.__familyStockCheckoutGuardInstalled) {
    window.__familyStockCheckoutGuardInstalled = true;
    document.addEventListener('click', (event) => {
      const checkout = event.target.closest?.('.cart__checkout-button, [name="checkout"]');
      if (!checkout) return;
      const root = checkout.closest('cart-drawer')?.querySelector('cart-drawer-items') || document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
      const violation = root ? firstViolation(root) : null;
      if (!violation) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) {
        errors.classList.remove('visually-hidden');
        errors.textContent = 'Your cart contains more units from one product family than are currently available. Reduce the quantity before checkout.';
      }
    }, true);
  }
  return { member, clampCartLine, limitProductAdd, firstViolation };
})();

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      const requestedFamilyQuantity = Math.max(1, Number.parseInt(formData.get('quantity'), 10) || 1);
      if (window.FamilyStockGuard) {
        try {
          const familyLimit = await window.FamilyStockGuard.limitProductAdd(this, formData.get('id'), requestedFamilyQuantity);
          if (familyLimit.quantity <= 0) {
            this.handleErrorMessage('There is not enough shared family stock available to add this quantity.');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
            return;
          }
          if (familyLimit.quantity < requestedFamilyQuantity) {
            formData.set('quantity', String(familyLimit.quantity));
            this.handleErrorMessage(`Only ${familyLimit.quantity} could be added because the other quantities in this product family use the same stock.`);
          }
        } catch (error) {
          console.error(error);
          this.handleErrorMessage('Unable to verify shared family stock. Please try again.');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          return;
        }
      }


      /* STOCKOPS FAMILY STOCK PREFLIGHT */
      if (window.StockOpsFamilyStock) {
        const stockValidation = await window.StockOpsFamilyStock.validateAdd(
          formData.get('id'),
          formData.get('quantity') || 1
        );
        if (!stockValidation || stockValidation.valid === false) {
          this.handleErrorMessage(stockValidation && stockValidation.message ? stockValidation.message : 'The requested quantity is not available.');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          return;
        }
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
